import { useState, useEffect } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { MoreHorizontal, X } from 'lucide-react';
import { orderedNavPages } from './navDefs';
import { useBarKeys } from '@/lib/useNavPrefs';
import { useHomeSummary } from '@/lib/useHomeSummary';

// Phones show 5 tabs; tablets (sm: ≥640px) show 8. The rest go in More.
const PHONE_TAB_COUNT = 5;
const TABLET_TAB_COUNT = 8;

function useIsTablet() {
  const [isTablet, setIsTablet] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = () => setIsTablet(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isTablet;
}

function isPageActive(to: string, pathname: string) {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

// 'action' = something to do (pink dot); 'unread' = new content to read (green dot).
type DotTone = 'action' | 'unread';

const DOT_CLASS: Record<DotTone, string> = {
  action: 'bg-candy-pink dark:bg-candy-pink-dark',
  unread: 'bg-candy-teal dark:bg-candy-teal-dark',
};

function useNavDots(): Map<string, DotTone> {
  const { data: summary } = useHomeSummary();
  // Chat unread now lives on the floating chat bubble (shell/ChatBubble), so
  // there is no bottom-nav chat/questions dot here.
  const dots = new Map<string, DotTone>();
  if (!summary) return dots;
  if ((summary.today?.pending_phases ?? 0) > 0) dots.set('/today', 'action');
  if ((summary.unread?.training ?? 0) > 0) dots.set('/resources', 'unread');
  if ((summary.unread?.promotions ?? 0) > 0) dots.set('/promotions', 'unread');
  if ((summary.unread?.recognition ?? 0) > 0) dots.set('/recognition', 'unread');
  return dots;
}

function Dot({ tone }: { tone: DotTone }) {
  return (
    <span
      className={`absolute -right-1 -top-0.5 h-2 w-2 rounded-full ${DOT_CLASS[tone]}`}
      aria-hidden
    />
  );
}

export function BottomNav() {
  const { pathname } = useLocation();
  const isTablet = useIsTablet();
  const [moreOpen, setMoreOpen] = useState(false);
  const navDots = useNavDots();

  // Close sheet on navigation.
  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  // Escape closes sheet.
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const barKeys = useBarKeys();
  const ordered = orderedNavPages(barKeys);
  const tabCount = isTablet ? TABLET_TAB_COUNT : PHONE_TAB_COUNT;
  const visibleTabs = ordered.slice(0, tabCount);
  const morePages = ordered.slice(tabCount);
  const isMoreActive = morePages.some((p) => isPageActive(p.to, pathname));

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-candy-line bg-candy-white dark:border-candy-dark-line dark:bg-candy-dark-surface"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label="Main navigation"
      >
        <div className="flex h-14 items-stretch">
          {visibleTabs.map((page) => {
            const active = isPageActive(page.to, pathname);
            const Icon = page.icon;
            return (
              <NavLink
                key={page.to}
                to={page.to}
                className={[
                  'flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium leading-tight',
                  active
                    ? 'text-candy-pink dark:text-candy-pink-dark'
                    : 'text-candy-ink-muted dark:text-candy-dark-ink-muted',
                ].join(' ')}
              >
                <span className="relative">
                  <Icon size={20} aria-hidden />
                  {navDots.has(page.to) && <Dot tone={navDots.get(page.to)!} />}
                </span>
                <span>{page.label}</span>
              </NavLink>
            );
          })}

          {morePages.length > 0 && (
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-label="More pages"
              aria-expanded={moreOpen}
              aria-haspopup="dialog"
              className={[
                'flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium leading-tight',
                isMoreActive || moreOpen
                  ? 'text-candy-pink dark:text-candy-pink-dark'
                  : 'text-candy-ink-muted dark:text-candy-dark-ink-muted',
              ].join(' ')}
            >
              <span className="relative">
                <MoreHorizontal size={20} aria-hidden />
                {(() => {
                  // More's dot summarizes the pages inside it: pink if anything
                  // needs action, green if there's only unread content.
                  const tones = morePages
                    .map((p) => navDots.get(p.to))
                    .filter((t): t is DotTone => t !== undefined);
                  if (tones.length === 0) return null;
                  return <Dot tone={tones.includes('action') ? 'action' : 'unread'} />;
                })()}
              </span>
              <span>More</span>
            </button>
          )}
        </div>
      </nav>

      {moreOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="All pages"
          className="fixed inset-0 z-40 flex items-end"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-black/40"
            onClick={() => setMoreOpen(false)}
          />
          {/* Sheet */}
          <div
            className="relative z-10 w-full rounded-t-2xl border-t border-candy-line bg-candy-white dark:border-candy-dark-line dark:bg-candy-dark-surface"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
          >
            <div className="flex items-center justify-between border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
              <span className="text-sm font-semibold text-candy-ink dark:text-candy-dark-ink">More</span>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                aria-label="Close"
                className="rounded-md p-1 text-candy-ink-muted hover:text-candy-ink dark:text-candy-dark-ink-muted dark:hover:text-candy-dark-ink"
              >
                <X size={20} aria-hidden />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1 px-2 py-3 sm:grid-cols-6">
              {morePages.map((page) => {
                const active = isPageActive(page.to, pathname);
                const Icon = page.icon;
                return (
                  <NavLink
                    key={page.to}
                    to={page.to}
                    className={[
                      'flex flex-col items-center gap-1.5 rounded-lg px-2 py-3 text-[11px] font-medium leading-tight',
                      active
                        ? 'bg-candy-cream-warm text-candy-pink dark:bg-candy-dark-surface-warm dark:text-candy-pink-dark'
                        : 'text-candy-ink-muted hover:bg-candy-cream-warm dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm',
                    ].join(' ')}
                  >
                    <span className="relative">
                      <Icon size={22} aria-hidden />
                      {navDots.has(page.to) && <Dot tone={navDots.get(page.to)!} />}
                    </span>
                    <span className="text-center">{page.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
